'use client';

import { ArrowLeft, X } from 'lucide-react';
import type React from 'react';

import { cn } from '@ui/lib/utils';

import { Dialog } from '../../../common/dialog/basicDialog';
import TickCircle from '../../../icons/TickCircle';
import { StarRating } from '../starRating/StarRating';

export interface ReviewDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  activityTitle?: string;
  date: string;
  rating: number;
  comment: string;
  tags?: string[];
  title?: string;
  userLikedLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

export const ReviewDetailDialog = ({
  isOpen,
  onClose,
  userName,
  activityTitle,
  date,
  rating,
  comment,
  tags,
  title = 'Customer Experience',
  userLikedLabel = 'User liked:',
  className,
  children
}: ReviewDetailDialogProps) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      size="sm"
      className={cn(
        'p-0 max-sm:!max-w-none max-sm:!w-screen max-sm:!h-dvh max-sm:!rounded-none max-sm:!translate-x-[-50%] max-sm:!translate-y-[-50%]',
        className
      )}
    >
      <div className="flex flex-col gap-4 p-6 max-sm:overflow-y-auto max-sm:h-full">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer p-1 hover:bg-neutral-100 rounded-full transition-colors"
          >
            <ArrowLeft className="text-neutral-900" size={20} />
          </button>
          <h3 className="text-lg font-semibold text-neutral-800 flex-1">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer hidden sm:block shrink-0 text-neutral-500 hover:text-neutral-800 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-base font-medium text-neutral-800">{userName}</p>
          {(activityTitle || date) && (
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              {activityTitle && <span>{activityTitle}</span>}
              {activityTitle && date && <span className="text-neutral-300">|</span>}
              {date && <span>{date}</span>}
            </div>
          )}
        </div>

        <StarRating value={rating} size="md" readonly />

        {comment && (
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap break-words">{comment}</p>
        )}

        {tags && tags.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-neutral-600 leading-normal">{userLikedLabel}</p>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 bg-neutral-100 border border-neutral-200 rounded pl-1.5 pr-2 py-1 text-xs text-neutral-700"
                >
                  <TickCircle className="size-3 shrink-0" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
        {children}
      </div>
    </Dialog>
  );
};
